<template>
    <div class="layout-container">
        <!-- Barra lateral izquierda -->
        <FormSidebar 
            :fields="fields"
            :data="data"
            :debugMode="debugMode"
            :defaultStyles="plantilla?.style"
            @select-field="selectField"
            @toggle-debug="toggleDebugMode"
            @export-form="exportForm"
            @import-form="importForm"
            @group-fields="groupFields"
            @delete-field="deleteField"
            @change-styles="handleChangeStyles"
        />
        
        <!-- Contenido principal -->
        <div class="main-content">
            <div class="form-container">
              <form @submit.prevent="handleSubmit(!v$.$invalid)" >
                    <div class="grid-container" style="margin-top: 40px;">
                    <!-- Zonas de drop para espacios vacíos -->
                    <template v-if="!this.isResizing && this.debugMode">
                         <div v-for="dropZone in dropZones" 
                             :key="`drop-${dropZone.row}-${dropZone.column}`"
                             :style="{ gridRow: dropZone.row, gridColumn: dropZone.column }"
                             class="drop-zone"  
                             @dragleave="handleDragLeaveEmptySpace($event, dropZone)"                                                                 
                             @dragenter="handleDragEnterEmptySpace($event, dropZone)"                                              
                             @drop="handleDropInEmptySpace($event, dropZone)"         
                             @dragover.prevent                   
                             :class="{ 'drop-zone-active': dropZone.isActive }">
                             <div class="drop-zone-indicator">
                                 <span class="drop-zone-text">Drop aquí ({{ dropZone.row }},{{ dropZone.column }})</span>
                             </div>
                         </div>
                     </template>

                     <!-- Botón para eliminar filas vacías -->
                     <div v-for="row in getEmptyRows()" 
                          :key="`empty-row-${row}`"
                          :style="{ gridRow: row, gridColumn: '1 / span 12' }"
                          class="empty-row-delete"
                          @dragover="handleDragOverEmptyRow($event, row)"
                          @dragenter="handleDragEnterEmptyRow($event, row)"
                          @dragleave="handleDragLeaveEmptyRow($event, row)"
                          @drop="handleDropInEmptyRow($event, row)"
                          :class="{ 'empty-row-drag-over': emptyRowDragOver === row }">
                         <div class="empty-row-info" v-if="emptyRowDragOver !== row">
                             <i class="pi pi-exclamation-triangle"></i>
                             <span>Fila {{ row }} completamente vacía</span>
                         </div>
                         <div class="empty-row-drop-info" v-if="emptyRowDragOver === row">
                             <i class="pi pi-arrow-down"></i>
                             <span>Soltar aquí para insertar</span>
                         </div>
                         <button @click="confirmDeleteRow(row)" 
                                 class="delete-row-btn"
                                 title="Eliminar fila vacía">
                             <i class="pi pi-trash"></i>
                             Eliminar fila
                         </button>
                     </div>

                     <DropField v-for="(field, fieldIndex) in fields"  
                                   :key="field.id"                  
                                   :field="field"     
                                   :debugMode="debugMode"    
                                   v-memo="[field.id, this.debugMode]"                                                             
                                   @field-drop="handleDrop($event, field)"        
                                   @field-startresize="startResize($event, field)">
                            <div class="field-content"> 
                               

                              <div v-if="field.idGroup">
                                <span class="p-float-label" style="font-weight: bold;" v-if="field.typeGroup === 'Checkbox'">     
                                   <label :for="field.name" style="margin-left: 20px;">{{ field.label }}</label>                
                                   <Checkbox
                                        :id="field.name" 
                                        :tabindex="fieldIndex"                                          
                                        value="true"                                                                                                    
                                        style="width: 100%;"
                                        v-model="v$.data[field.name].$model"   
                                        @focus="handleFieldFocus($event, field)"
                                        @blur="handleFieldBlur($event, field)"
                                        :class="{ 'p-invalid': v$.data[field.name].$invalid && submitted }"  
                                    />
                                </span> 
                                <span class="p-float-label" style="font-weight: bold;" v-if="field.typeGroup === 'Radio'">     
                                   <label :for="field.name" style="margin-left: 20px;">{{ field.name }}</label>                      
                                   <RadioButton
                                        :id="field.name" 
                                        :name="field.idGroup" 
                                        :tabindex="fieldIndex"                                          
                                        :value="field.name"                                                                                                    
                                        style="width: 100%; min-width: 100px;"
                                        v-model="v$.data[field.nameGroup].$model"   
                                        @focus="handleFieldFocus($event, field)"
                                        @blur="handleFieldBlur($event, field)"
                                        :class="{ 'p-invalid': v$.data[field.name].$invalid && submitted }"  
                                    />
                                </span> 
                                <span class="p-float-label" style="font-weight: bold;" v-if="field.typeGroup === 'List' && field.visible">     
                                  <Dropdown    
                                        :id="field.name"  
                                        :tabindex="fieldIndex+1"
                                        :options="JSON.parse(field.options)"
                                        optionLabel="name"
                                        optionValue="value"
                                        style="width: 100%;"
                                        v-model="v$.data[field.name].$model" 
                                        @focus="handleFieldFocus($event, field)"
                                        @blur="handleFieldBlur($event, field)"
                                        :class="{ 'p-invalid': v$.data[field.name].$invalid && submitted }"                                 
                                    />     
                                    <Label :field="field" />       
                                </span> 
                              </div>      
                              <div v-else>
                                <span class="p-float-label" style="font-weight: bold;" v-if="field.type === 'String'">                    
                                    <InputText
                                        :id="field.name"                                           
                                        :tabindex="fieldIndex+1"
                                        type="text"
                                        style="width: 100%;"
                                        v-model="v$.data[field.name].$model" 
                                        @focus="handleFieldFocus($event, field)"
                                        @blur="handleFieldBlur($event, field)"
                                        :class="{ 'p-invalid': v$.data[field.name].$invalid && submitted }"  
                                    />
                                    <Label :field="field" />     
                  
                                </span> 
                                <span class="p-float-label" style="font-weight: bold;" v-if="field.type === 'Number'">                    
                                    <InputText
                                        :id="field.name"                                           
                                        :tabindex="fieldIndex+1"
                                        type="number"
                                        style="width: 100%;"
                                        v-model="v$.data[field.name].$model" 
                                        @focus="handleFieldFocus($event, field)"
                                        @blur="handleFieldBlur($event, field)"
                                        :class="{ 'p-invalid': v$.data[field.name].$invalid && submitted }"  
                                    />
                                    <Label :field="field" />
                                </span> 
                                <span class="p-float-label" style="font-weight: bold;" v-if="field.type === 'Date'">                    
                                    <Calendar
                                        :id="field.name"   
                                        :tabindex="fieldIndex+1"
                                        v-model="v$.data[field.name].$model" 
                                        @focus="handleFieldFocus($event, field)"
                                        @blur="handleFieldBlur($event, field)"
                                        :class="{ 'p-invalid': v$.data[field.name].$invalid && submitted }"  
                                    />
                                    <Label :field="field" />
                                </span>     
                                <span class="p-label" style="font-weight: bold" v-if="field.type === 'Boolean'">   
                                    <label :for="field.name" style="margin-right: 10px;">{{ field.label }}</label>                 
                                    <InputSwitch
                                        :id="field.name" 
                                        :tabindex="fieldIndex"  
                                        v-model="v$.data[field.name].$model" 
                                        @focus="handleFieldFocus($event, field)"
                                        @blur="handleFieldBlur($event, field)"
                                        :class="{ 'p-invalid': v$.data[field.name].$invalid && submitted }" 
                                        
                                    />                    
                                </span>   
                                <span class="p-float-label" style="font-weight: bold" v-if="field.type === 'List'">                                                    
                                    <Dropdown    
                                        :id="field.name"  
                                        :tabindex="fieldIndex+1"
                                        :options="JSON.parse(field.options)"
                                        optionLabel="name"
                                        optionValue="value"
                                        style="width: 100%;"
                                        v-model="v$.data[field.name].$model" 
                                        @focus="handleFieldFocus($event, field)"
                                        @blur="handleFieldBlur($event, field)"
                                        :class="{ 'p-invalid': v$.data[field.name].$invalid && submitted }"                                 
                                    />     
                                    <Label :field="field" />                              
                                </span>  

                                <div v-if="field.type === 'Label'" @click="startEditingLabel(field)" style="cursor: pointer;">
                                    <h3 v-if="!field.isEditing">{{ field.label }}</h3>
                                    <input 
                                        v-if="field.isEditing"
                                        v-model="field.label"
                                        @blur="finishEditingLabel(field)"
                                        @keyup.enter="finishEditingLabel(field)"
                                        @keyup.esc="cancelEditingLabel(field)"
                                        ref="labelInput"
                                        style="width: 100%; font-size: 1.5rem; font-weight: bold; border: 2px solid #2196f3; padding: 8px; border-radius: 4px;"
                                        autofocus
                                    />
                                </div>

                                <div v-if="field.type === 'Image'"> 
                                  <img :src="field.options" alt="Image" @click="openImage(event, field)" style="width: 100%; height: 100%; object-fit: cover;"/>
                                </div>
                            
                                <div v-if="this.isInput(field)">
                                    <small v-if="(v$.data[field.name].$invalid && submitted) || v$.data[field.name].$pending.$response" class="p-error">
                                         <div v-for="error of v$.data[field.name].$errors" :key="error.$uid">
                                        {{ error.$message }}
                                        </div>
                                    </small> 
                                </div>
                              </div>
                            </div>            
                    </DropField>
                  </div>

                  <div class="p-d-flex p-jc-center p-mt-4" v-if="!this.debugMode">                    
                    <div class="p-mt-4"><Button type="submit" label="Enviar"/></div>
                  </div>
            </form>
        </div>
        </div>
    
    </div>
</template>  

<script>

import { apiGet, apiPost, apiDelete, apiPut } from '../../helpers/rest-api'
import useVuelidate from '@vuelidate/core';
import { v4 as uuidv4 } from 'uuid';
//import { maxValue, helpers, required, minValue, maxLength, minLength } from '@vuelidate/validators';
import { generarReglasDesdeJSON } from '../../helpers/validations'; 

import DROP_POSITION from '../../enum/FormDesign';

import FormSidebar from '../../components/form/Sidebar.vue';
//import DragDrogComponent from '../../components/DragDrogComponent.vue';
//import { reactive, computed } from 'vue'

import Label from './Label.vue';
import DropField from '../../components/DropField.vue';

const reglasJSON = [
        {
          field: 'NOMBREAPELLIDO',
          rules: [{ type: 'required', message: 'Campo obligatorio' }]
        },{
            field: 'CIF',       
            rules: [
                { type: 'required', message: 'Campo obligatorio' },
                { type: 'minValue', value: 11, message: 'El valor debe ser mayor a :minValue' },
                { type: 'maxValue', value: 100, message: 'El valor debe ser menor a :maxValue' },
            ]
        },
        {
          field: 'PAIS',          
          rules: [
                {
                    type: 'requiredIfAll', // 👈 clave aquí
                    conditions: [
                    { field: 'NOMBREAPELLIDO', operator: '==', value: 'A' },
                    { field: 'CIF', operator: '>', value: 18 }
                    ],
                    message: ':property es obligatorio si NOMBREAPELLIDO es A o CIF > 18'
                }
            ]
        },
        {
          field: 'ESTADOCIVIL',          
          rules: [
                {
                    type: 'requiredIf', // 👈 clave aquí
                    field: 'NOMBREAPELLIDO', 
                    operator: '==', 
                    value: 'A' ,
                    message: ':property es obligatorio si NOMBREAPELLIDO es A'
                }
            ]
        }
      ]

export default {
    components: {
        Label,
        DropField,
        FormSidebar,
        
    },
    setup() {

    },
    data () {
        return { 
                v$: null,
                plantilla: undefined,
                fields : [], 
                data: {}, 
                submitted: false,
                dropZoneState: {},
                // Propiedades para resize
                isResizing: false,
                resizingField: null,
                startX: 0,
                startSize: 0,
                // Propiedades para información de campo
                fieldInfo: {
                    visible: false,
                    text: '',
                    x: 0,
                    y: 0
                },
                styles: '',
                // Propiedades para drop zones
                dropZones: [],
                dropZoneTimer: null,
                // Propiedades para línea de inserción
                showInsertLine: true,
                insertLinePosition: 0,  
                DROP_POSITION,
                // Propiedad para tracking de drag over en filas vacías
                emptyRowDragOver: null,
                // Propiedad para modo debug
                debugMode: true
            }
        },
        created() {
      
        },
        mounted () {
            if (this.$route.params.id) {
                this.getPlantilla(this.$route.params.id)       
            }
        },
        methods: {
            // Toggle del modo debug
            toggleDebugMode() { 
               this.debugMode = !this.debugMode;

               this.resetStyles();
            },
            
        async getPlantilla(idPlantilla) {
           
            if (!idPlantilla) {
                console.error('No se encontró el ID de la plantilla')
                return
            }

            const resPlantilla = await apiGet(`plantilla/${idPlantilla}`)

            this.plantilla = resPlantilla.data;

            const res = await apiGet(`field/plantilla/${idPlantilla}`)

            const fields = this.widthToSize(res.data);    

            const fieldsRowZero = fields.filter(field => field.Row === 0);

            let fieldsGroupedAndOrdered = [];

            if (fieldsRowZero.length !== 0) {
               fieldsGroupedAndOrdered = this.groupByTopAndOrderByLeft(fields);
               fieldsGroupedAndOrdered.forEach(field => {
                this.saveField(field);
               });
            }else{
              fieldsGroupedAndOrdered = fields;
            }

            // Inicializar propiedades de drag & drop
            fieldsGroupedAndOrdered.forEach(field => {
                field.isDragOver = false;
                field.dropPosition = null;
                field.isSelected = false;
            });

            this.fields = fieldsGroupedAndOrdered;
            
            // Calcular drop zones después de cargar los campos
            this.calculateDropZones();
           // console.log('Drop zones inicializadas:', this.dropZones.length);
            
             // Inicializamos los datos
            this.data = this.fields.reduce((acc, field) => {
                acc[field.name] = field.type === 'Boolean' || field.idGroup ? false : ''
                
                return acc
            }, {})

            // ⚠ Generás reglas dinámicamente
            const rules = { data: generarReglasDesdeJSON(this.fields, reglasJSON, this)}

            // ⚠ Ahora sí, inicializás Vuelidate manualmente
            this.v$ = useVuelidate(rules, this)
        },  

   
        handleFieldFocus(event) {
            // Cambiar el color de fondo del campo cuando toma el foco
            if (this.debugMode) {
               event.target.style.backgroundColor = '#e3f2fd';
               event.target.style.borderColor = '#2196f3';
            
            
              // También cambiar el color del label
              const label = event.target.parentElement.querySelector('label');
              if (label) {
                label.style.color = '#2196f3';
              }
            }
        },
        handleFieldBlur(event) {
            // Restaurar el color de fondo cuando pierde el foco
            if (this.debugMode) {
              event.target.style.backgroundColor = '';
              event.target.style.borderColor = '';
            
              // Restaurar el color del label
              const label = event.target.parentElement.querySelector('label');

              if (label) {
                  label.style.color = '';
              }
            }
        },

        // Label editing
        startEditingLabel(field) {
            // Guardar el valor original para poder cancelar
            field.originalLabel = field.label;
            field.isEditing = true;
            
            // Enfocar el input en el siguiente tick para que Vue renderice el input
            this.$nextTick(() => {
                const input = this.$refs.labelInput;
                if (input) {
                    input.focus();
                    input.select(); // Seleccionar todo el texto
                }
            });
            
           // console.log('Iniciando edición del label:', field.name);
        },
        finishEditingLabel(field) {
            field.isEditing = false;
            delete field.originalLabel;

            this.saveField(field);
            
           // console.log('Finalizando edición del label:', field.name, 'Nuevo valor:', field.label);
        },
        cancelEditingLabel(field) {
            // Restaurar el valor original
            field.label = field.originalLabel;
            field.isEditing = false;
            delete field.originalLabel;
            
           // console.log('Cancelando edición del label:', field.name);
        },

        // Drag & Drop
        handleDrop(event, targetField) {
            console.log('handleDrop', event);

            event.preventDefault();
           // Obtener los datos del drag
            const dragData = event.dataTransfer.getData('text/plain');

            try {
                const parsedData = JSON.parse(dragData);

                const field = this.fields.find(f => f.id === parsedData.id);
              
                 //Si es un nuevo elemento
                if (parsedData.type === 'newElement' && !field) {
                    const id = this.insertNewElement(parsedData.id, parsedData.elementType, targetField);

                    const draggedField = this.fields.find(f => f.id === id);

             

                    // Solo modificar row y column, no cambiar el orden del array
                    this.recalculatePositionsAfterInsert(targetField, draggedField); 

                    this.clearDragStates();

                    return;
                }
            } catch (e) {
                // Si no es JSON, es un campo existente
                const draggedFieldId = dragData;
                const draggedField = this.fields.find(f => f.id === draggedFieldId);

         
                
                if (draggedField && draggedField.id !== targetField.id) {
                    // Solo modificar row y column, no cambiar el orden del array
                    this.recalculatePositionsAfterInsert(targetField, draggedField);
 
                }
            }
            
            // Limpiar estados visuales
            this.clearDragStates();

            
        },

        // Clear drag states
        clearDragStates() {
            // Limpiar timer
            clearTimeout(this.dropZoneTimer);
            
            this.fields.forEach(field => {
                field.isDragOver = false;
                field.dropPosition = null;
            });
            this.dropZones.forEach(zone => {
                zone.isActive = false;
            });
            
            // Ocultar línea de inserción
            this.showInsertLine = false;
        },
        calculateDropZones() {
            // Calcular espacios vacíos en el grid
            const maxRow = Math.max(...this.fields.map(f => f.row));
            const occupiedPositions = new Set();
            
            // Marcar posiciones ocupadas
            this.fields.forEach(field => {
                for (let i = 0; i < field.size; i++) {
                    occupiedPositions.add(`${field.row}-${field.column + i}`);
                }
            });
            
            // Crear drop zones en espacios vacíos
            this.dropZones = [];
            for (let row = 1; row <= maxRow ; row++) {
                for (let col = 1; col <= 12; col++) {
                    const position = `${row}-${col}`;
                    if (!occupiedPositions.has(position)) {
                        this.dropZones.push({
                            id: uuidv4(),
                            row: row,
                            column: col,
                            isActive: false
                        });
                    }
                }
            }
            
           // console.log('Drop zones calculadas:', this.dropZones.length, 'zonas');
        },
        // Detectar filas que están completamente vacías (solo drop zones)
        getEmptyRows() {
            const maxRow = Math.max(...this.fields.map(f => f.row), 0);
            const emptyRows = [];
            
            for (let row = 1; row <= maxRow; row++) {
                const fieldsInRow = this.fields.filter(field => field.row === row);
                if (fieldsInRow.length === 0) {
                    // Verificar si hay drop zones en esta fila
                    const dropZonesInRow = this.dropZones.filter(zone => zone.row === row);
                    if (dropZonesInRow.length > 0) {
                        // Verificar si la fila está completamente vacía (12 drop zones)
                        if (dropZonesInRow.length === 12) {
                            emptyRows.push(row);
                        }
                    }
                }
            }
            
            // También verificar filas que solo tienen drop zones pero están después de la última fila con campos
            const lastRowWithFields = Math.max(...this.fields.map(f => f.row), 0);
            for (let row = lastRowWithFields + 1; row <= maxRow + 1; row++) {
                const dropZonesInRow = this.dropZones.filter(zone => zone.row === row);
                if (dropZonesInRow.length === 12) {
                    emptyRows.push(row);
                }
            }
            
            // Filtrar filas que ya no están vacías (por si acaso)
            return emptyRows.filter(row => {
                const fieldsInRow = this.fields.filter(field => field.row === row);
                return fieldsInRow.length === 0;
            });
        },

        // Confirmar eliminación de fila vacía
        confirmDeleteRow(rowNumber) {
            if (confirm(`¿Estás seguro de que quieres eliminar la fila ${rowNumber}? Esta acción no se puede deshacer.`)) {
                this.deleteEmptyRow(rowNumber);
            }
        },

        // Drag & Drop en filas vacías
        handleDragOverEmptyRow(event) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
        },
        handleDragEnterEmptyRow(event, rowNumber) {
            event.preventDefault();
            this.emptyRowDragOver = rowNumber;
        },
        handleDragLeaveEmptyRow(event) {
            // Solo limpiar si realmente salimos del elemento
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.emptyRowDragOver = null;
            }
        },
        handleDropInEmptyRow(event, rowNumber) {
            event.preventDefault();
            this.emptyRowDragOver = null;

           // console.log('Drop en fila vacía:', rowNumber);
           const dragData = event.dataTransfer.getData('text/plain');

           try {
                const parsedData = JSON.parse(dragData);
               // console.log('Datos parseados en fila vacía:', parsedData);
                
                // Si es un nuevo elemento
                if (parsedData.type === 'newElement') {
                   // console.log('Insertando nuevo elemento en fila vacía:', parsedData.elementType);
                    this.insertNewElementInEmptyRow(parsedData.elementType, rowNumber);
                    return;
                }
            } catch (e) {
               // console.log('No es JSON, tratando como campo existente en fila vacía');
                // Si no es JSON, es un campo existente
                const draggedFieldId = dragData;
                const draggedField = this.fields.find(f => f.id === draggedFieldId);
                
                if (draggedField) {
                   // console.log('Campo existente movido a fila vacía:', draggedField.name);
                    
                    // Verificar si el campo ya está en esta fila
                    if (draggedField.row === rowNumber) {
                       // console.log('El campo ya está en esta fila');
                        return;
                    }
                    
                    // Mover el campo a la fila vacía
                    draggedField.row = rowNumber;
                    draggedField.column = 1; // Comenzar desde la primera columna

                    this.saveField(draggedField);
                    
                    // Recalcular posiciones
                    this.calculateDropZones();
                    
                   // console.log(`Campo ${draggedField.name} movido a fila vacía: Fila ${rowNumber}, Col 1`);
                } else {
                   // console.log('Campo no encontrado con ID:', draggedFieldId);
                }
            }
        },

        // Insertar nuevo elemento en una fila vacía
        insertNewElementInEmptyRow(elementType, rowNumber) {
            const id = uuidv4();

            const newField = {
                id,
                name: `${elementType.toLowerCase()}_${Date.now()}`,
                type: elementType,
                label: `Nuevo ${elementType}`,
                row: rowNumber,
                column: 1, // Comenzar desde la primera columna
                size: 4,
                order: this.fields.length + 1,
                page: 1,
                isSelected: false,
                isDragOver: false,
                dropPosition: null
            };

            this.saveField(newField);

            // Insertar el nuevo campo
            this.fields.push(newField);
            
            // Recalcular drop zones
            this.calculateDropZones();
            
           // console.log('Nuevo elemento insertado en fila vacía:', newField.name, 'en fila', newField.row, 'columna', newField.column);
            
            return id;
        },

        // Eliminar una fila vacía
        deleteEmptyRow(rowNumber) {
            console.log('Eliminando fila vacía:', rowNumber);
           
            // Eliminar todas las drop zones de esta fila
            this.dropZones = this.dropZones.filter(zone => zone.row !== rowNumber);
            
            // Mover todos los campos de filas posteriores hacia arriba
            this.fields.forEach(field => {
                if (field.row > rowNumber) {
                    field.row -= 1;
                    this.saveField(field);
                }                
            });
            
            // Mover todas las drop zones de filas posteriores hacia arriba
            this.dropZones.forEach(zone => {
                if (zone.row > rowNumber) {
                    zone.row -= 1;
                }
            });
           
            // Recalcular drop zones para asegurar consistencia
            this.calculateDropZones();
            
           // console.log('Fila eliminada y posiciones recalculadas');
        },

        // Drag & Drop Empty Space
        handleDragOverEmptySpace(event) {
            event.preventDefault();
           // console.log('handleDragOverEmptySpace', event);
            
            
            // Permitir el drop en espacios vacíos           
         //   event.dataTransfer.dropEffect = 'copy';
            
            // Calcular posición de la línea de inserción
          //  this.calculateInsertLinePosition(event, dropZone);
        },
        handleDragEnterEmptySpace(event, dropZone) {
        
            // Limpiar timer anterior
          //  this.dropZones.forEach(zone => zone.isActive = false);
            
            // Activar la drop zone
            dropZone.isActive = true;
            
        },
        handleDragLeaveEmptySpace(event, dropZone) {
            event.preventDefault();
           // console.log('Drop zone Empty Space desactivada', event, dropZone.id);
            dropZone.isActive = false;
        },
        
        calculateInsertLinePosition(event, dropZone) {
            // Obtener el contenedor del formulario
            const formContainer = document.querySelector('.grid-container');
            if (!formContainer) return;
            
            // Obtener la posición del contenedor
            const containerRect = formContainer.getBoundingClientRect();
            
            // Calcular la posición Y de la línea basada en la fila del drop zone
            const rowHeight = 80; // Altura aproximada de cada fila
            const linePosition = containerRect.top + (dropZone.row - 1) * rowHeight + 40; // +40 para centrar en la fila
            
            // Mostrar la línea de inserción
            this.showInsertLine = true;
            this.insertLinePosition = linePosition;
            
           // console.log('Línea de inserción en posición:', linePosition, 'fila:', dropZone.row);
        },
        handleDropInEmptySpace(event, dropZone) {

            console.log('handleDropInEmptySpace');

            event.preventDefault();
            
            const dragData = event.dataTransfer.getData('text/plain');

           // console.log('dragData:', dragData);
            
            try {
                const parsedData = JSON.parse(dragData);

               // console.log('Datos parseados:', parsedData);
                
                // Si es un nuevo elemento
                if (parsedData.type === 'newElement') {
                   // console.log('Insertando nuevo elemento:', parsedData.elementType);
                    this.insertNewElementInEmptySpace(parsedData.elementType, dropZone);
                    this.dropZones.forEach(zone => zone.isActive = false);
                    return;
                }
            } catch (e) {
               // console.log('No es JSON, tratando como campo existente');
                // Si no es JSON, es un campo existente
                const draggedFieldId = dragData;
                const draggedField = this.fields.find(f => f.id === draggedFieldId);
                
                if (draggedField) {
                   // console.log('Campo encontrado:', draggedField.name);
                    // Mover el campo a la posición vacía
                    draggedField.row = dropZone.row;
                    draggedField.column = dropZone.column;

                    this.saveField(draggedField);
                    
                    // Recalcular posiciones
                    this.calculateDropZones();
                    
                   // console.log(`Campo ${draggedField.name} movido a espacio vacío: Fila ${dropZone.row}, Col ${dropZone.column}`);
                } else {
                   // console.log('Campo no encontrado con ID:', draggedFieldId);
                }
            }
            
            // Limpiar estados
            this.dropZones.forEach(zone => zone.isActive = false);
        },

        recalculatePositionsAfterInsert(targetField, draggedField) {
            // Asignar la fila y columna correctas al campo movido
            draggedField.row = targetField.row; 
            // Determinar la columna basada en la posición de inserción

            if (targetField.dropPosition === DROP_POSITION.AFTER) {
                // Si es 'after', insertar después del campo objetivo
                draggedField.column = targetField.column + targetField.size;

                this.saveField(draggedField);

                this.fields.forEach(field => {
                    if (field.row === targetField.row && field.id !== draggedField.id && field.column > targetField.column) {
                        field.column += draggedField.size;
                        this.saveField(field);
                    }
                });               
            }

            if (targetField.dropPosition === DROP_POSITION.BEFORE) {
                
                // Mover el campo objetivo una columna hacia la derecha
  
                draggedField.column = targetField.column;

                this.saveField(draggedField);

                this.fields.forEach(field => {
                    if (field.row === targetField.row && field.id !== draggedField.id && field.column >= targetField.column) {
                        field.column += draggedField.size;
                        this.saveField(field);
                    }
                });
            } 

            if (targetField.dropPosition === DROP_POSITION.UP) {
                // Si es 'up', insertar arriba del campo objetivo

                draggedField.row = targetField.row;
                draggedField.column = targetField.column;
                this.saveField(draggedField);

                this.fields.forEach(field => {
                    if (field.id !== draggedField.id && field.row >= targetField.row) {
                        field.row +=1;
                        this.saveField(field);
                    }
                });    
                
                this.calculateDropZones();
            
            }

            if (targetField.dropPosition === DROP_POSITION.DOWN) {
                // Si es 'up', insertar arriba del campo objetivo

                draggedField.row = targetField.row + 1;
                draggedField.column = targetField.column;

                this.saveField(draggedField);

                this.fields.forEach(field => {
                    if (field.id !== draggedField.id && field.row > targetField.row) {
                        field.row += 1;
                        this.saveField(field);
                    }
                });   
                
                this.calculateDropZones();
            
            }        
        },

   
        // Resize
        startResize(event, field) {
            event.preventDefault();
            event.stopPropagation();
            
            this.isResizing = true;
            this.resizingField = field;
            this.startX = event.clientX || event.touches[0].clientX;
            this.startSize = field.size;
            
            // Agregar clase para estilos de resize
            const fieldElement = event.target.closest('.draggable-field');
            if (fieldElement) {
                fieldElement.classList.add('resizing');
            }
            
            document.addEventListener('mousemove', this.handleResize);
            document.addEventListener('mouseup', this.stopResize);
            document.addEventListener('touchmove', this.handleResize);
            document.addEventListener('touchend', this.stopResize);
        },
        handleResize(event) {
            if (!this.isResizing || !this.resizingField) return;
            
            event.preventDefault();
            
            const currentX = event.clientX || event.touches[0].clientX;
            const deltaX = currentX - this.startX;
            
            // Calcular el nuevo tamaño basado en el movimiento
            const gridColumnWidth = 100 / 12; // 12 columnas en el grid
            const sizeChange = Math.round(deltaX / (window.innerWidth * gridColumnWidth / 100));
            
            let newSize = this.startSize + sizeChange;
            
            // Limitar el tamaño entre 1 y 12
            newSize = Math.max(1, Math.min(12, newSize));
            
            // Verificar que no se exceda de 12 columnas en la fila
            const fieldsInRow = this.fields.filter(f => f.row === this.resizingField.row);
            const totalSizeInRow = fieldsInRow.reduce((sum, f) => sum + (f.id === this.resizingField.id ? newSize : f.size), 0);
            
            if (totalSizeInRow <= 12) {
                this.resizingField.size = newSize;
            }
        },
        stopResize() {
            this.isResizing = false;
            this.resizingField = null;
            
            // Remover clase de resize
            const fieldElements = document.querySelectorAll('.draggable-field.resizing');

            fieldElements.forEach(element => {
              if (element.classList.contains('resizing')) {
                element.classList.remove('resizing');
                const field = this.fields.find(f => f.id === element.dataset.fieldId);
                if (field) {
                  this.saveField(field);
                }
              }
            });

            this.calculateDropZones();
            
            document.removeEventListener('mousemove', this.handleResize);
            document.removeEventListener('mouseup', this.stopResize);
            document.removeEventListener('touchmove', this.handleResize);
            document.removeEventListener('touchend', this.stopResize);
        },

        // Width to Size
        widthToSize(fields) {
            const WIDTH_PDF_A4 = 595.28;

            return fields.map((field) => {
              if (field.size == undefined || field.size == 0) {
                const percentage = field.width * 100 / WIDTH_PDF_A4;
                const size = Math.round(percentage * 12 / 100 );
                field.size = size == 0 ? 1 : size;
              } 
              
              return field;
            });
        },


        
        // Sidebar methods
        getUniqueRows() {
            const rows = [...new Set(this.fields.map(f => f.row))].sort((a, b) => a - b);
            return rows;
        },
        
        getUniqueFieldTypes() {
            const types = [...new Set(this.fields.map(f => f.type))];
            return types;
        },
        

        // Exportar formulario
        exportForm() {
            const formData = {
                fields: this.fields,
                data: this.data
            };
            const dataStr = JSON.stringify(formData, null, 2);
            const dataBlob = new Blob([dataStr], {type: 'application/json'});
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'formulario.json';
            link.click();
            URL.revokeObjectURL(url);
           // console.log('Formulario exportado');
        },
        
        importForm() {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.json';
            input.onchange = (event) => {
                const file = event.target.files[0];
                if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        try {
                            const formData = JSON.parse(e.target.result);
                            this.fields = formData.fields || [];
                            this.data = formData.data || {};
                            this.calculateDropZones();
                           // console.log('Formulario importado');
                        } catch (error) {
                            console.error('Error al importar:', error);
                        }
                    };
                    reader.readAsText(file);
                }
            };
            input.click();
        },
        
        selectField({ event, field }) {
            // Limpiar selección anterior
            if (event.shiftKey === true) {
                field.isSelected = !field.isSelected;
            } else {
              this.fields.forEach(f => f.isSelected = false);    
              field.isSelected = true;
            }
    
            // Hacer scroll al campo en el formulario
            this.$nextTick(() => {
                const fieldElement = document.querySelector(`[data-field-id="${field.id}"]`);
                if (fieldElement) {
                    fieldElement.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center' 
                    });
                    
                    // Aplicar efecto visual de selección
                    fieldElement.style.boxShadow = '0 0 0 3px rgba(0, 123, 255, 0.5)';
                    fieldElement.style.borderColor = '#007bff';
                    
                    // Remover el efecto después de 2 segundos
                    setTimeout(() => {
                        fieldElement.style.boxShadow = '';
                        fieldElement.style.borderColor = '';
                    }, 2000);
                }
            });
            
           // console.log('Campo seleccionado:', field.name);
        },
        
        
        insertNewElement(id, elementType, targetField) {


            const newField = {
                id,
                idPlantilla: this.plantilla.id,
                name: `${elementType.toLowerCase()}_${Date.now()}`,
                type: elementType,
                label: `Nuevo ${elementType}`,
                row: targetField.row,
                column: targetField.column,
                size: 4,
                order: this.fields.length + 1,
                page: targetField.page || 1,
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                validation: '',
                value: '',
                options: '[]',
                isDragOver: false,
                dropPosition: null,
                isSelected: false
            };
            
            // Mover campos posteriores
            /*
            this.fields.forEach(field => {
                if (field.row === targetField.row && field.column >= targetField.column) {
                    field.column += newField.size;
                }
            });
            */

            this.saveField(newField);
            
            // Insertar el nuevo campo
            this.fields.push(newField);
            
            // Recalcular drop zones
            //this.calculateDropZones();

            return id;            
        },
        
        insertNewElementInEmptySpace(elementType, dropZone) {

            const newField = {
                id: uuidv4(),
                idPlantilla: this.plantilla.id,
                name: `${elementType.toLowerCase()}_${Date.now()}`,
                type: elementType,
                label: `Nuevo ${elementType}`,
                row: dropZone.row,
                column: dropZone.column,
                size: 1,
                order: this.fields.length + 1,
                page: 1,
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                validation: '',
                value: '',
                options: '[]',
                isDragOver: false,
                dropPosition: null,
                isSelected: true
            };

            this.saveField(newField);
            
            // Insertar el nuevo campo
            this.fields.push(newField);
            
            // Recalcular drop zones
            this.calculateDropZones();
            
           // console.log('Nuevo elemento insertado en espacio vacío:', newField.name, 'en fila', newField.row, 'columna', newField.column);
        },
        
        // Group by top and order by left
        groupByTopAndOrderByLeft(fields) {
            const fieldsWithPage = fields.filter(field => field.page !== undefined && field.page !== null);

            if (fieldsWithPage.length === 0) {
                console.error('No hay campos con posición top');
                return fields;
            }

            // Filtrar campos que tienen top
            const fieldsWithTop = fields.filter(field => field.top !== undefined && field.top !== null);
            
            if (fieldsWithTop.length === 0) {
               // console.log('No hay campos con posición top');
                return fields;
            }
            
            // Agrupar campos por page primero
            const pageGroups = {};
            
            fields.forEach(field => {
                const page = field.page;
                if (!pageGroups[page]) {
                    pageGroups[page] = [];
                }
                pageGroups[page].push(field);
            });
            
            // Ordenar páginas y procesar cada una
            const orderedFields = [];
            let currentOrder = 0;
            
            const sortedPages = Object.keys(pageGroups).sort((a, b) => parseInt(a) - parseInt(b));
            
            let row = 1;

            //Pages
            sortedPages.forEach((page) => {
                const _pageFields = pageGroups[page];
                
                // Agrupar campos por top con tolerancia de ±5px dentro de cada página
                const groupsTop = [];
                const tolerance = 8;
                
                // Top
                _pageFields.forEach(field => {
                    let groupFound = false;
                    
                    // Buscar un grupo existente donde el campo pueda pertenecer
                    for (let groupTop of groupsTop) {
                        const top = groupTop[0].top;
                        if (Math.abs(field.top - top) <= tolerance) {
                            groupTop.push(field);
                            groupFound = true;
                            break;
                        }
                    }
                    
                    // Si no se encontró grupo, crear uno nuevo
                    if (!groupFound) {
                        groupsTop.push([field]);
                    }
                });

                // Ordenar grupos por top (de arriba hacia abajo)
                groupsTop.sort((a, b) => a[0].top - b[0].top);
                
                // Mantener el espacio disponible de la fila anterior
              
          
                //
                groupsTop.forEach((groupTop) => {
                    // Ordenar el grupo por left (de izquierda a derecha)
                    groupTop.sort((a, b) => (a.left || 0) - (b.left || 0));
                    
                   let column = 1;
                               // Agregar campos ordenados al resultado
                    
                    groupTop.forEach((field) => {
                       //currentRowSize += field.size;
                       field.order=  currentOrder; 
          
                       //field.row = pageIndex * 1000 + rowIndex + 1;
                       //field.row = pageIndex == 0 ? 0 : groupsTop.length + rowIndex + 1;
                       field.row = row;
                       field.column = column; //columnIndex == 0 ? 0 : currentRowSize + 1;
               
                       orderedFields.push(field); 
                       currentOrder++   

                       column += field.size;                    
                    });

                    row += 1;
                   
                });

                row += 1;
            });
            
            // Agregar campos que no tienen top al final
            /*
            const fieldsWithoutTop = fields.filter(field => field.top === undefined || field.top === null);
            fieldsWithoutTop.forEach(field => {
                orderedFields.push({
                    ...field,
                    order: currentOrder
                });
                currentOrder++;
            });
            */
            
           // console.log('Páginas procesadas:', sortedPages);
           // console.log('Campos ordenados por page, top y left:', orderedFields);
            
            return orderedFields;
        },

        saveField(field) {
            console.log('saveField', field);
            apiPost('field', JSON.stringify(field));
        },

        groupFields (idGroup){

          const options = this.fields.reduce((acc, field) => {
            if (field.idGroup === idGroup && field.typeGroup === 'List') {
              acc.push(`{"name": "${field.name}", "value": "${field.name}"}`);
            }
            return acc;
          }, []);

          let first = false;

          this.fields.map(field => {
            if (field.idGroup === idGroup && field.typeGroup === 'List') {         
              field.options =  '[' + options.join(',') + ']';

              field.visible = first ? false : true;
              first = true;
            }
          });

          let nameGroup = undefined;

          this.fields.map(field => {
            if (field.idGroup === idGroup && field.typeGroup === 'Radio') {                      
              field.nameGroup = nameGroup || field.name;
              nameGroup = field.nameGroup;
              field.visible = true;
            }
          });

          this.fields.map(field => {
            if (field.idGroup === idGroup) {                      
              this.saveField(field);
            }
          });

        },

          // Handle Submit
          handleSubmit(isFormValid) {


            const processItemId = '7780e788-bef6-4511-8594-f2ae22f80ee6';
            
            this.submitted = true;
            this.v$.$touch()

            if (!isFormValid) {
                console.warn('Formulario no valido');
                return;
            }

            const fields = this.fields.map((field) => {
                const value = field.type === 'Date' ? this.data[field.name].toISOString() : String(this.data[field.name]);
                return {...field, value: value}
            });

            apiPost(`form/${processItemId}/false`, JSON.stringify(fields)).then((data) => console.log(data));
        }, 

        isInput(field) {
            return field.type !== 'Label' && field.type !== 'Image';
        },

        deleteField(field) {
          this.fields = this.fields.filter(f => f.id !== field.id);
          apiDelete(`field/${field.id}`);
        },

        openImage(event, field) {
          // Crear un input file oculto
          const fileInput = document.createElement('input');
          fileInput.type = 'file';
          fileInput.accept = 'image/*';
          fileInput.style.display = 'none';
          
          // Agregar el input al DOM
          document.body.appendChild(fileInput);
          
          // Configurar el evento change
          fileInput.onchange = (e) => {
            
            const file = e.target.files[0];

            if (file) {
              // Verificar que sea una imagen
              if (!file.type.startsWith('image/')) {
                alert('Por favor selecciona un archivo de imagen válido');
                return;
              }
              
              // Crear un FileReader para leer la imagen
              const reader = new FileReader();
              reader.onload = (event) => {
                const imageData = event.target.result;
                
                // Actualizar el campo con la nueva imagen
                field.options = imageData;
                //field.imageName = file.name;
                //field.imageSize = file.size;
                
                // Guardar en la API si es necesario
                this.saveFieldImage(field);
                
              };
              
              reader.readAsDataURL(file);
            }
            
            // Limpiar el input file
            document.body.removeChild(fileInput);
          };
          
          // Simular clic en el input file
          fileInput.click();
        },
        
        saveFieldImage(field) {
          try {
            // Aquí puedes implementar la lógica para guardar la imagen en la API
            // Por ejemplo:
            // apiPost(`field/${field.id}/image`, {
            //   imageData: field.imageData,
            //   imageName: field.imageName
            // });

          
            apiPost('field', JSON.stringify(field));
            console.log('Guardando imagen para el campo:', field.id);
          } catch (error) {
            console.error('Error al guardar la imagen:', error);
          }
        },

        handleChangeStyles(style) {
          console.log('handleChangeStyles', style);
          
          if (this.plantilla) {
            this.plantilla.style = style;
            apiPut(`plantilla/${this.plantilla.id}`, JSON.stringify(this.plantilla));
          }
          this.resetStyles();
        },  

        resetStyles() {
          const el = document.getElementById("style-form-editor")
            if (el) {
              el.remove() 
            }

          if (!this.debugMode) {
              const style = document.createElement("style")
              style.id = "style-form-editor";
              style.textContent = ".form-container { " + this.plantilla.style + "}";
              document.head.appendChild(style);
          }
        }
    }
}

/**/


//

</script>


<style scoped>
/* Layout principal */
.layout-container {
  display: flex;
  height: 100vh;
  overflow: hidden;
}



/* Contenido principal */
.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: #ffffff;
}

.p-error{
    padding-top: 5px;
    font-size: 14px;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;
}

.grid-item {
  /* estilos opcionales */
}

/* Estilos para transiciones suaves en los campos */
.p-float-label input,
.p-float-label .p-inputtext,
.p-float-label .p-calendar,
.p-float-label .p-dropdown,
.p-label .p-inputswitch {
  transition: all 0.3s ease;
}

.p-float-label label,
.p-label label {
  transition: color 0.3s ease;
}

/* Estilos para SectionLabel */
div[style*="cursor: pointer"] h3 {
  transition: all 0.3s ease;
  padding: 8px;
  border-radius: 4px;
  margin: 0;
}

div[style*="cursor: pointer"] h3:hover {
  background-color: #f5f5f5;
  border: 1px dashed #ccc;
}

/* Estilos para el input de edición */
div[style*="cursor: pointer"] input {
  transition: all 0.3s ease;
  outline: none;
}

div[style*="cursor: pointer"] input:focus {
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}


/* Estilos para drop zones en espacios vacíos */
.drop-zone {

  border: 2px dashed transparent;
  border-radius: 4px;
  transition: all 0.3s ease;
  position: relative;
}

.drop-zone:hover {
  border-color: #3498db;
  background-color: rgba(52, 152, 219, 0.1);
}

.drop-zone-active {
  border-color: #2ecc71 !important;
  background-color: rgba(46, 204, 113, 0.2) !important;
  animation: pulse 1s infinite;
}

.drop-zone-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.drop-zone:hover .drop-zone-indicator,
.drop-zone-active .drop-zone-indicator {
  opacity: 1;
}

.drop-zone-text {
  font-size: 10px;
  color: #3498db;
  font-weight: 500;
  text-align: center;
  pointer-events: none;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(46, 204, 113, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(46, 204, 113, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(46, 204, 113, 0);
  }
}

/* Estilos para filas vacías y botón de eliminar */
.empty-row-delete {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 80px;
  background-color: rgba(231, 76, 60, 0.1);
  border: 2px dashed #e74c3c;
  border-radius: 8px;
  position: relative;
  padding: 16px;
}

.empty-row-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e74c3c;
  font-size: 14px;
  font-weight: 500;
}

.empty-row-info i {
  font-size: 16px;
}

.empty-row-drop-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #3498db;
  font-size: 14px;
  font-weight: 500;
  animation: fadeIn 0.3s ease;
}

.empty-row-drop-info i {
  font-size: 16px;
  color: #3498db;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Estado de drag over para filas vacías */
.empty-row-delete.empty-row-drag-over {
  background-color: rgba(52, 152, 219, 0.2);
  border-color: #3498db;
  border-style: solid;
  transform: scale(1.02);
  transition: all 0.2s ease;
}

.empty-row-delete.empty-row-drag-over .empty-row-info {
  color: #3498db;
}

.empty-row-delete.empty-row-drag-over .empty-row-info i {
  animation: bounce 0.6s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-5px);
  }
  60% {
    transform: translateY(-3px);
  }
}

/* Estilos para la fila de eliminación */

.delete-row-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(231, 76, 60, 0.3);
}

.delete-row-btn:hover {
  background-color: #c0392b;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(231, 76, 60, 0.4);
}

.delete-row-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(231, 76, 60, 0.3);
}

.delete-row-btn i {
  font-size: 14px;
}

/* Estilos para la barra de inserción */
.insert-bar {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #3498db;
  border-radius: 2px;
  z-index: 10;
  animation: insertBarPulse 1s infinite;
}

.insert-bar-left {
  left: 0;
}

.insert-bar-right {
  right: 0;
}

@keyframes insertBarPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(52, 152, 219, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(52, 152, 219, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(52, 152, 219, 0);
  }
}

/* Estilos para la línea de inserción */
.insert-line {    
  width: 100%;
  height: 3px;
  background-color: #db3434;
  z-index: 1000;
  pointer-events: none;
  animation: insertLinePulse 1s infinite;
}

@keyframes insertLinePulse {
  0% {
    box-shadow: 0 0 0 0 rgba(52, 152, 219, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(52, 152, 219, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(52, 152, 219, 0);
  }
}

/* Estilos para controles de fila */
.row-controls {
  position: relative;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(52, 152, 219, 0.1);
  border: 1px dashed #3498db;
  border-radius: 4px;
  margin: 4px 0;
  transition: all 0.3s ease;
}

.row-controls:hover {
  background-color: rgba(52, 152, 219, 0.2);
  border-color: #2980b9;
}

.row-controls-container {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
}

.row-control-btn {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 12px;
}

.add-row-btn {
  background-color: #27ae60;
  color: white;
}

.add-row-btn:hover {
  background-color: #229954;
  transform: scale(1.1);
}

.remove-row-btn {
  background-color: #e74c3c;
  color: white;
}

.remove-row-btn:hover {
  background-color: #c0392b;
  transform: scale(1.1);
}

.row-number {
  font-size: 12px;
  font-weight: 500;
  color: #3498db;
  background-color: rgba(52, 152, 219, 0.1);
  padding: 2px 6px;
  border-radius: 3px;
}

.field-content {
  width: 100%;
  position: relative;  
}


</style>

<!--
 :{
                NOMBREAPELLIDO: {
                    required
                },
                PAIS: {},
                CEA: {},
                CIF: {},
                CONFIRMA: {},
                FECHA: {},
                LUGAR: {},
               
            }

            order: ${field.order}, 

            gridColumn: `${field.column} / span ${field.size}`,

-->